import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Spinner } from 'flowbite-react';
import { api } from '../api';
import { DashboardStats } from '../types/dashboard';

const StatCard: React.FC<{ title: string; value: number | string }> = ({ title, value }) => (
  <Card className="transition-transform hover:scale-105">
    <h5 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
      {title}
    </h5>
    <p className="text-3xl font-semibold text-gray-700 dark:text-gray-400">
      {value}
    </p>
  </Card>
);

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await api.dashboard.getStats();
        setStats(data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <h1 className="mb-8 text-3xl font-bold text-gray-900 dark:text-white">
        Dashboard Overview
      </h1>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard title="Total Users" value={stats?.statistics.totalUsers || 0} />
        <StatCard title="Total Loans" value={stats?.statistics.totalLoans || 0} />
        <StatCard 
          title="Total Documents" 
          value={stats?.statistics.totalDocuments || 0} 
        />
      </div>

      <Card>
        <div className="flex items-center justify-between">
          <h5 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
            Recent Loans
          </h5>
        </div>
        <div className="flow-root">
          <ul className="divide-y divide-gray-200">
            {stats?.recentLoans.map((loan) => (
              <li 
                key={loan.id} 
                className="cursor-pointer py-3 transition-colors hover:bg-gray-50 sm:py-4"
                onClick={() => navigate(`/users/${loan.user_id}`)}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 font-medium">
                        {loan.first_name[0]}
                      </span>
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                      {loan.first_name} {loan.last_name}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {loan.loan_purposes.map((purpose: string, idx: number) => (
                        <span key={idx} className="text-xs text-gray-500">
                          {purpose}
                        </span>
                      ))}
                    </div>
                    <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                      {loan.email}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div className="text-base font-semibold text-green-600">
                      SAR {loan.borrow_amount.toLocaleString()}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between">
          <h5 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
            Recent Documents
          </h5>
        </div>
        <div className="flow-root">
          <ul className="divide-y divide-gray-200">
            {stats?.recentDocuments.map((doc) => (
              <li 
                key={doc.document_id} 
                className="cursor-pointer py-3 transition-colors hover:bg-gray-50 sm:py-4"
                onClick={() => navigate(`/users/${doc.user_id}`)}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <span className="text-purple-600 font-medium">
                        {doc.file_name[0]}
                      </span>
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                      {doc.file_name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                        {doc.document_type}
                      </span>
                      <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                        {doc.first_name}
                      </p>
                    </div>
                  </div>
                  <div className="inline-flex items-center text-sm text-gray-500">
                    {new Date(doc.created_at).toLocaleDateString()}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default Dashboard; 